import { useState, useEffect } from "react";
import { format, parseISO } from "date-fns";
import { Trash2, ChevronRight, Building2, Calendar, Users, TrendingUp, Loader2, FileSpreadsheet, Download, CalendarRange } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { fetchShifts, deleteShift } from "@/lib/api";
import { CPT_CODES, WRVU_MAP, formatNumber, formatCurrency } from "@/lib/constants";
import { useTheme } from "@/contexts/ThemeContext";
import ThemeToggle from "@/components/ThemeToggle";

const API_URL = process.env.REACT_APP_BACKEND_URL || "";

// Helper function to get auth token
const getAuthToken = () => {
  const authData = localStorage.getItem("wrvu_auth") || sessionStorage.getItem("wrvu_auth");
  return authData ? JSON.parse(authData).token : null;
};

// Export range options
const EXPORT_RANGES = [
  { id: "current_month", label: "Current Month" },
  { id: "last_month", label: "Last Month" },
  { id: "last_quarter", label: "Last Quarter" },
  { id: "ytd", label: "Year-to-Date" },
];

export default function History({ refreshKey, retentionRate = 0.76, wrvuRate = 25 }) {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const [shifts, setShifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);
  
  // Export state
  const [exportingRange, setExportingRange] = useState(null);
  const [showCustomDateRange, setShowCustomDateRange] = useState(false);
  const [customStartDate, setCustomStartDate] = useState("");
  const [customEndDate, setCustomEndDate] = useState("");

  const loadShifts = async () => {
    try {
      setLoading(true);
      const data = await fetchShifts();
      setShifts(data);
    } catch (err) {
      toast.error("Failed to load shift history");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadShifts();
  }, [refreshKey]);

  // Export handler
  const handleExport = async (rangeId) => {
    const token = getAuthToken();
    if (!token) {
      toast.error("Please log in to export data");
      return;
    }
    
    setExportingRange(rangeId);
    try {
      const res = await fetch(`${API_URL}/api/shifts/export/csv?range=${rangeId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Export failed");
      }
      
      const contentDisposition = res.headers.get("Content-Disposition");
      let filename = "shift_data.csv";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Shift data exported successfully");
    } catch (err) {
      toast.error(err.message || "Failed to export data");
    } finally {
      setExportingRange(null);
    }
  };

  // Custom date range export handler
  const handleCustomExport = async () => {
    if (!customStartDate || !customEndDate) {
      toast.error("Please select both start and end dates");
      return;
    }
    
    if (new Date(customStartDate) > new Date(customEndDate)) {
      toast.error("Start date must be before end date");
      return;
    }
    
    const token = getAuthToken();
    if (!token) {
      toast.error("Please log in to export data");
      return;
    }
    
    setExportingRange("custom");
    try {
      const res = await fetch(`${API_URL}/api/shifts/export/csv?range=custom&start=${customStartDate}&end=${customEndDate}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.detail || "Export failed");
      }
      
      const contentDisposition = res.headers.get("Content-Disposition");
      let filename = "shift_data.csv";
      if (contentDisposition) {
        const match = contentDisposition.match(/filename=(.+)/);
        if (match) filename = match[1];
      }
      
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success("Shift data exported successfully");
      setShowCustomDateRange(false);
      setCustomStartDate("");
      setCustomEndDate("");
    } catch (err) {
      toast.error(err.message || "Failed to export data");
    } finally {
      setExportingRange(null);
    }
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    try {
      await deleteShift(id);
      setShifts(prev => prev.filter(s => s.id !== id));
      toast.success("Shift deleted");
    } catch (err) {
      toast.error("Failed to delete shift");
    }
  };

  const toggleExpand = (id) => {
    setExpandedId(prev => prev === id ? null : id);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className={`h-6 w-6 animate-spin ${isDark ? 'text-slate-500' : 'text-gray-500'}`} />
      </div>
    );
  }

  if (shifts.length === 0) {
    return (
      <div data-testid="empty-history" className="text-center py-16">
        <div className={`inline-flex items-center justify-center w-16 h-16 rounded-2xl mb-4 ${
          isDark ? 'bg-slate-800/50' : 'bg-gray-100'
        }`}>
          <Calendar className={`h-8 w-8 ${isDark ? 'text-slate-600' : 'text-gray-400'}`} />
        </div>
        <p className={`text-lg ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>No shifts recorded yet</p>
        <p className={`text-sm mt-1 ${isDark ? 'text-slate-600' : 'text-gray-500'}`}>Start tracking to see your history here</p>
      </div>
    );
  }

  return (
    <div data-testid="shift-history" className="space-y-6 pb-32">
      {/* Header with Theme Toggle */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className={`text-3xl md:text-4xl font-bold tracking-tight mb-1 ${
            isDark ? 'text-slate-100' : 'text-gray-900'
          }`} style={{ fontFamily: "'Manrope', sans-serif" }}>
            Shift History
          </h1>
          <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>
            View and manage your recorded shifts
          </p>
        </div>
        <ThemeToggle />
      </div>

      {/* Export Shift Data Section */}
      <div data-testid="export-shift-data" className={`rounded-xl border p-6 ${
        isDark 
          ? 'border-slate-700/30 bg-slate-800/30' 
          : 'border-gray-200 bg-white'
      }`}>
        <div className="flex items-center gap-2 mb-2">
          <FileSpreadsheet size={18} className={isDark ? 'text-amber-400' : 'text-amber-600'} />
          <span className={`text-lg font-semibold ${isDark ? 'text-slate-100' : 'text-gray-900'}`}>Export Shift Data</span>
        </div>
        <p className={`text-sm mb-6 ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>Download your wRVUs, Hourly Income, and Patient Visits as a CSV file compatible with Excel</p>
        
        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
          {EXPORT_RANGES.map((range) => (
            <Button
              key={range.id}
              data-testid={`export-${range.id}`}
              onClick={() => handleExport(range.id)}
              disabled={exportingRange !== null}
              className={isDark 
                ? "bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 hover:border-amber-500/50 text-slate-200 hover:text-amber-400 transition-all"
                : "bg-gray-100 hover:bg-gray-200 border border-gray-200 hover:border-amber-500 text-gray-700 hover:text-amber-600 transition-all"
              }
            >
              {exportingRange === range.id ? (
                <div className={`animate-spin h-4 w-4 border-2 border-t-transparent rounded-full mr-2 ${
                  isDark ? 'border-amber-400' : 'border-amber-600'
                }`} />
              ) : (
                <Download size={16} className="mr-2" />
              )}
              {range.label}
            </Button>
          ))}
          <Button
            data-testid="export-custom"
            onClick={() => setShowCustomDateRange(!showCustomDateRange)}
            disabled={exportingRange !== null}
            className={`${isDark 
              ? "bg-slate-700/50 hover:bg-slate-700 border border-slate-600/50 hover:border-amber-500/50 text-slate-200 hover:text-amber-400"
              : "bg-gray-100 hover:bg-gray-200 border border-gray-200 hover:border-amber-500 text-gray-700 hover:text-amber-600"
            } transition-all ${showCustomDateRange ? (isDark ? 'border-amber-500/50 text-amber-400' : 'border-amber-500 text-amber-600') : ''}`}
          >
            <CalendarRange size={16} className="mr-2" />
            Custom Range
          </Button>
        </div>
        
        {/* Custom Date Range Picker */}
        {showCustomDateRange && (
          <div className={`mt-4 p-4 rounded-lg border ${
            isDark 
              ? 'bg-slate-900/50 border-slate-700/50' 
              : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex flex-wrap items-end gap-4">
              <div className="flex-1 min-w-[140px]">
                <label className={`text-xs mb-1 block ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>From Date</label>
                <Input
                  type="date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  className={isDark 
                    ? "bg-slate-800/50 border-slate-700/50 focus:border-amber-500/50 text-slate-200"
                    : "bg-white border-gray-300 focus:border-amber-500 text-gray-900"
                  }
                />
              </div>
              <div className="flex-1 min-w-[140px]">
                <label className={`text-xs mb-1 block ${isDark ? 'text-slate-500' : 'text-gray-600'}`}>To Date</label>
                <Input
                  type="date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  className={isDark 
                    ? "bg-slate-800/50 border-slate-700/50 focus:border-amber-500/50 text-slate-200"
                    : "bg-white border-gray-300 focus:border-amber-500 text-gray-900"
                  }
                />
              </div>
              <Button
                onClick={handleCustomExport}
                disabled={exportingRange === "custom" || !customStartDate || !customEndDate}
                className="bg-amber-600 hover:bg-amber-500 text-white"
              >
                {exportingRange === "custom" ? (
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                ) : (
                  <Download size={16} className="mr-2" />
                )}
                Export
              </Button>
            </div>
          </div>
        )}
      </div>

      {/* Shift History List */}
      <div className="space-y-3">
      {shifts.map((shift) => {
        const isExpanded = expandedId === shift.id;
        let displayDate;
        try {
          displayDate = format(parseISO(shift.date), "EEEE, MMM d, yyyy");
        } catch {
          displayDate = shift.date;
        }

        return (
          <div
            key={shift.id}
            data-testid={`shift-entry-${shift.id}`}
            className={`rounded-xl border overflow-hidden transition-colors ${
              isDark 
                ? 'border-slate-700/30 bg-slate-800/30 hover:bg-slate-800/50' 
                : 'border-gray-200 bg-white hover:bg-gray-50'
            }`}
          >
            <div
              className="flex items-center justify-between p-4 cursor-pointer"
              onClick={() => toggleExpand(shift.id)}
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1">
                  <Calendar className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  <span className={`text-sm font-medium truncate ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>{displayDate}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Building2 className={`h-3.5 w-3.5 flex-shrink-0 ${isDark ? 'text-slate-500' : 'text-gray-400'}`} />
                  <span className={`text-xs truncate ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{shift.clinic_location}</span>
                </div>
              </div>

              <div className="flex items-center gap-4 flex-shrink-0">
                <div className="text-right space-y-0.5">
                  <div className="flex items-center gap-1.5">
                    <Users className="h-3 w-3 text-blue-400" />
                    <span className={`text-sm font-mono font-bold ${isDark ? 'text-blue-400' : 'text-blue-600'}`}>{shift.total_patients?.toLocaleString()}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <TrendingUp className="h-3 w-3 text-emerald-400" />
                    <span className={`text-sm font-mono font-bold ${isDark ? 'text-emerald-400' : 'text-emerald-600'}`}>{formatNumber(shift.total_wrvus || 0)}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <span className={`text-[10px] font-semibold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>$</span>
                    <span className={`text-xs font-mono font-bold ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>{formatCurrency(shift.total_wrvus * wrvuRate + (shift.total_hourly_pay || 0)).substring(1)}</span>
                  </div>
                </div>

                <Button
                  data-testid={`delete-shift-${shift.id}`}
                  size="icon"
                  variant="ghost"
                  onClick={(e) => handleDelete(shift.id, e)}
                  className={`h-8 w-8 ${
                    isDark 
                      ? 'text-slate-600 hover:text-red-400 hover:bg-red-500/10' 
                      : 'text-gray-400 hover:text-red-600 hover:bg-red-50'
                  }`}
                >
                  <Trash2 size={14} />
                </Button>

                <ChevronRight
                  size={16}
                  className={`transition-transform duration-200 ${isExpanded ? 'rotate-90' : ''} ${
                    isDark ? 'text-slate-600' : 'text-gray-400'
                  }`}
                />
              </div>
            </div>

            {isExpanded && (
              <div className={`px-4 pb-4 border-t pt-3 ${isDark ? 'border-slate-700/20' : 'border-gray-100'}`}>
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
                  {CPT_CODES.map(cpt => {
                    const count = shift.tallies?.[cpt.code] || 0;
                    if (count === 0) return null;
                    return (
                      <div key={cpt.code} className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${
                        isDark ? 'bg-slate-700/20' : 'bg-gray-100'
                      }`}>
                        <span className={`font-mono ${isDark ? 'text-slate-400' : 'text-gray-600'}`}>{cpt.label}</span>
                        <div className="text-right">
                          <span className={`font-mono font-semibold ${isDark ? 'text-slate-200' : 'text-gray-900'}`}>{count}</span>
                          <span className={`ml-1 ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>({formatNumber(count * (WRVU_MAP[cpt.code] || 0))})</span>
                        </div>
                      </div>
                    );
                  })}
                  {(shift.misc_entries || []).map((entry, i) => (
                    <div key={entry.id || i} className={`flex items-center justify-between text-xs px-3 py-2 rounded-lg ${
                      isDark ? 'bg-purple-500/10' : 'bg-purple-50'
                    }`}>
                      <span className={`font-mono truncate mr-2 ${isDark ? 'text-purple-400' : 'text-purple-700'}`}>{entry.label || `Misc #${i + 1}`}</span>
                      <span className={`font-mono font-semibold ${isDark ? 'text-purple-300' : 'text-purple-800'}`}>{formatNumber(parseFloat(entry.wrvus) || 0)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        );
      })}
      </div>
    </div>
  );
}
